import { createEntityId } from '@/core/EntityId';
import type { Command } from '@/core/Command';
import { CompositeCommand } from '@/core/CompositeCommand';
import type { CommandHistory } from '@/core/CommandHistory';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import { clampMaturity, type FloraInstance } from '@/flora/FloraInstance';
import type { FloraWorld } from '@/flora/FloraWorld';
import type { Terrain } from '@/terrain/Terrain';
import {
  clipRect,
  findSurfaceLayer,
  isEmptyRect,
  isPlantableSurface,
  unionRect,
  EMPTY_RECT,
  type SurfaceRect,
} from '@/terrain/TerrainSurface';
import { distanceToRing, pointInRing, ringBounds } from '@/site/SiteFootprint';
import { createSemanticTag, type TagTarget } from '@/semantics/SemanticTag';
import type { TagImage } from '@/semantics/TagImage';
import type { TagStore } from '@/semantics/TagStore';
import { AddTagCommand } from '@/editor/TagCommands';
import type { PlanPlanting } from '@/reference/SitePlan';
import type { PlanStructure, SurfaceRegionPlan } from '@/reference/SitePipeline';
import { readHeightRect, sculptLimits, vertexToWorld } from '@/terrain/TerrainSculpt';
import type { SiteObject } from '@/site/SiteObject';
import type { SiteObjectWorld } from '@/site/SiteObjectWorld';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import {
  AddManyFloraCommand,
  RemoveFloraCommand,
  TransformFloraCommand,
  type FloraChange,
} from '@/editor/FloraCommands';
import { PaintStrokeCommand, SculptStrokeCommand } from '@/editor/TerrainCommands';
import { AddSiteObjectsCommand, RemoveSiteObjectsCommand } from '@/editor/SiteObjectCommands';
import { SetEnvironmentCommand } from '@/editor/EnvironmentCommands';
import { paintSurfaces, buildStructures } from '@/reference/SitePipelineCompiler';
import type { CompileWarning } from '@/reference/SitePipelineCompiler';
import {
  mulberry32,
  targetPositions,
  tryPlace,
  type PlacementContext,
  type PlacementRefusal,
} from '@/reference/PlantingPlacement';
import type {
  AdjustPlantsOp,
  PlanTag,
  PlantTarget,
  RemovePlantsOp,
  SculptOp,
  TagOp,
  WandOp,
  WandSpell,
} from './WandOps';

/**
 * Turn a {@link WandSpell} into one undoable edit of the **live** site.
 *
 * ## The division of labour, again
 *
 * The same one the reference pipeline runs on: the model supplies the judgements that need someone
 * to have understood a sentence, and this module does every piece of arithmetic those judgements
 * imply. It samples the ground, spaces the planting, keeps it out of walls and off blacktop, converts
 * outlines into local frames, and clamps everything to the site.
 *
 * ## Why one command and not several
 *
 * A spell is one thing the user asked for, so it is one thing they can take back. Every op becomes a
 * normal editor command — the same `SculptStrokeCommand` the brush produces, the same
 * `AddManyFloraCommand` that paste produces — and they are wrapped in a {@link CompositeCommand}.
 * That is the property that makes applying a wand result immediately, without a confirmation step,
 * an acceptable design: Ctrl+Z is a complete answer to a bad one.
 *
 * It also means nothing here is a special case downstream. The site does not know it was edited by a
 * wand; it holds plants, ground and buildings that serialize, re-render, re-season and re-light like
 * any other, because at the point of application they *are* the same records.
 *
 * ## Never throws on a merely questionable spell
 *
 * A species this build does not have, a polygon that runs off the site, an id that no longer exists:
 * dropped and **reported**. The report is what the panel shows, and it is the compiler's word rather
 * than the model's — the model says what it meant to do, this says what happened.
 */

export interface WandApplyDeps {
  terrain: Terrain;
  world: FloraWorld;
  catalog: FloraCatalog;
  siteObjects: SiteObjectWorld;
  environment: EnvironmentManager;
  /** Where a `tag` op's statements land — the same store the tag tool writes to. */
  tags: TagStore;
  /**
   * Photographs attached to this request, already downscaled and encoded.
   *
   * Passed in rather than read from the panel so the compiler stays testable without a DOM, and so
   * the rule about what happens to them is stated in one place: a picture is copied onto a tag only
   * when the spell asks for it by index. Nothing is attached implicitly — an image the model never
   * mentioned is evidence for the request, not a permanent statement about the site.
   */
  attachments?: readonly TagImage[];
  /** The live selection, resolved at apply time so `"selected": true` means what the user sees. */
  selectedIds: () => readonly string[];
  /** Varies planting layout between casts. Defaults to a clock-free constant for testability. */
  seed?: number;
  /** Called with the vertex rect after the ground moved, so plants and buildings re-seat. */
  onGroundChanged?: (rect: SurfaceRect) => void;
}

/** What actually happened — the compiler's account, not the model's. */
export interface WandReport {
  plantsAdded: number;
  plantsRemoved: number;
  plantsAdjusted: number;
  structuresAdded: number;
  structuresRemoved: number;
  tagsAdded: number;
  /** Photographs moved off the request and onto a tag, where they outlive the turn. */
  tagImagesAttached: number;
  /** Square metres of ground whose height changed. */
  sculptedAreaMeters: number;
  /** Metres the ground moved at the point that moved most. */
  maxHeightChangeMeters: number;
  paintedRegions: number;
  environmentChanged: boolean;
  warnings: CompileWarning[];
  /** Ops that produced nothing at all, named so a silent no-op is impossible. */
  inertOps: string[];
}

export interface WandCompileResult {
  command: CompositeCommand | null;
  report: WandReport;
}

export function emptyWandReport(): WandReport {
  return {
    plantsAdded: 0,
    plantsRemoved: 0,
    plantsAdjusted: 0,
    structuresAdded: 0,
    structuresRemoved: 0,
    tagsAdded: 0,
    tagImagesAttached: 0,
    sculptedAreaMeters: 0,
    maxHeightChangeMeters: 0,
    paintedRegions: 0,
    environmentChanged: false,
    warnings: [],
    inertOps: [],
  };
}

/**
 * Compile a spell without applying it. Every command is built against the current state and left
 * un-executed, so the caller decides when — and whether — the site changes.
 */
export function compileWandSpell(spell: WandSpell, deps: WandApplyDeps): WandCompileResult {
  const report = emptyWandReport();
  const commands: Command[] = [];
  const rand = mulberry32(Math.max(1, Math.trunc(deps.seed ?? 1)));
  // Plan id → the id actually minted, so a `tag` op can reach what a `build` op earlier in the same
  // spell created. Ops are compiled in the order the model wrote them, which is what makes this
  // work: describing a thing before tagging it is the order a person would write it in too.
  const minted = new Map<string, string>();

  for (const op of spell.ops) {
    const before = commands.length;
    compileOp(op, deps, rand, commands, report, minted);
    if (commands.length === before) report.inertOps.push(op.kind);
  }

  for (const dropped of spell.dropped) {
    report.warnings.push({ message: `The wand returned ${dropped}; it was ignored.` });
  }

  if (commands.length === 0) return { command: null, report };
  return { command: new CompositeCommand(commands, wandLabel(spell)), report };
}

/** Compile and push onto the history in one step — the path the UI takes. */
export function applyWandSpell(
  spell: WandSpell,
  deps: WandApplyDeps,
  history: CommandHistory,
): WandReport {
  const { command, report } = compileWandSpell(spell, deps);
  if (command) history.execute(command);
  return report;
}

/**
 * The history label.
 *
 * Named after what the wand said it did rather than "Wand", because the undo tooltip is where the
 * user looks a minute later to work out which entry to take back. "Undo Wand" answers nothing.
 */
function wandLabel(spell: WandSpell): string {
  const summary = spell.summary.trim().replace(/\s+/g, ' ');
  if (summary.length === 0) return 'Wand';
  const short = summary.length > 48 ? `${summary.slice(0, 47).trimEnd()}…` : summary;
  return `Wand: ${short.charAt(0).toLowerCase()}${short.slice(1)}`;
}

function compileOp(
  op: WandOp,
  deps: WandApplyDeps,
  rand: () => number,
  commands: Command[],
  report: WandReport,
  minted: Map<string, string>,
): void {
  switch (op.kind) {
    case 'sculpt':
      compileSculpt(op, deps, commands, report);
      break;
    case 'paint':
      compilePaint(op.regions, deps, commands, report);
      break;
    case 'plant':
      compilePlant(op.plantings, deps, rand, commands, report);
      break;
    case 'remove-plants':
      compileRemove(op, deps, commands, report);
      break;
    case 'adjust-plants':
      compileAdjust(op, deps, commands, report);
      break;
    case 'build':
      compileBuild(op.structures, deps, commands, report, minted);
      break;
    case 'tag':
      compileTags(op, deps, commands, report, minted);
      break;
    case 'remove-structures': {
      const objects = op.ids
        .map((id) => deps.siteObjects.get(id))
        .filter((object): object is SiteObject => object !== undefined);
      if (objects.length < op.ids.length) {
        report.warnings.push({
          message: `${op.ids.length - objects.length} structure(s) the wand named are not on this site.`,
        });
      }
      if (objects.length === 0) break;
      commands.push(new RemoveSiteObjectsCommand(deps.siteObjects, objects));
      report.structuresRemoved += objects.length;
      break;
    }
    case 'environment':
      compileEnvironment(op, deps, commands, report);
      break;
  }
}

/* --------------------------------- Sculpt --------------------------------- */

/**
 * Blend control points into the existing ground.
 *
 * The kernel is the pipeline's: smoothstep falloff to zero at each point's own radius, so adjacent
 * points merge into one landform rather than showing a seam, and a reading about the back bank cannot
 * tilt the front lawn. What differs is the target. The pipeline always drives toward an absolute
 * elevation because it is building ground that does not exist yet; the wand's default is `relative`,
 * because on a site that already has a slope, "raise a berm here" means raise it *from the slope* —
 * an absolute reading would flatten the very ground the berm is supposed to sit on.
 *
 * The "after" heights are computed into a separate buffer rather than by mutating and reverting, so
 * the live field is never briefly wrong and there is nothing to undo if compilation fails later.
 *
 * ## Areas
 *
 * An area contributes to the same weighted blend as a point, measured from its boundary rather than
 * from a centre: full weight anywhere inside the outline, smoothstepping to zero `falloffMeters`
 * outside it. Because it is the same accumulator, a shape and a point in one op merge into one
 * landform — "raise this bed and put a knoll at its north end" is one sculpt, not two seams.
 */
function compileSculpt(
  op: SculptOp,
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
): void {
  const field = deps.terrain.field;
  const points = op.points.filter((point) => {
    const inside =
      Math.abs(point.x) <= field.widthMeters / 2 + point.radiusMeters &&
      Math.abs(point.z) <= field.lengthMeters / 2 + point.radiusMeters;
    if (!inside) {
      report.warnings.push({
        message: `A sculpt point at (${round(point.x, 1)}, ${round(point.z, 1)}) lies off the site and was ignored.`,
      });
    }
    return inside;
  });

  const areas = (op.areas ?? []).map((area) => ({
    ring: area.polygon.map(([x, z]) => ({ x, z })),
    elevationMeters: area.elevationMeters,
    falloffMeters: area.falloffMeters,
  }));

  if (points.length === 0 && areas.length === 0) return;

  let rect: SurfaceRect = { ...EMPTY_RECT };
  for (const point of points) {
    rect = unionRect(rect, vertexBounds(field.cols, field.rows, field.widthMeters, field.lengthMeters, point));
  }
  for (const area of areas) {
    const bounds = ringBounds(area.ring);
    rect = unionRect(
      rect,
      vertexBounds(field.cols, field.rows, field.widthMeters, field.lengthMeters, {
        x: (bounds.minX + bounds.maxX) / 2,
        z: (bounds.minZ + bounds.maxZ) / 2,
        radiusMeters:
          Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / 2 + area.falloffMeters,
      }),
    );
  }
  rect = clipRect(rect, field.cols, field.rows);
  if (isEmptyRect(rect)) return;

  const before = readHeightRect(field, rect);
  const after = Float32Array.from(before);
  const { minHeight, maxHeight } = sculptLimits(deps.terrain.params.elevationAmplitude);

  let touched = 0;
  let maxDelta = 0;
  for (let row = 0; row < rect.height; row += 1) {
    for (let col = 0; col < rect.width; col += 1) {
      const world = vertexToWorld(field, rect.x + col, rect.y + row);
      const index = row * rect.width + col;
      const current = before[index] ?? 0;

      let weightSum = 0;
      let targetSum = 0;
      for (const point of points) {
        const distance = Math.hypot(world.x - point.x, world.z - point.z);
        if (distance >= point.radiusMeters) continue;
        const t = 1 - distance / point.radiusMeters;
        const weight = t * t * (3 - 2 * t);
        weightSum += weight;
        targetSum += weight * (op.mode === 'relative' ? current + point.elevationMeters : point.elevationMeters);
      }
      for (const area of areas) {
        const outside = pointInRing(area.ring, world.x, world.z)
          ? 0
          : distanceToRing(area.ring, world.x, world.z);
        if (outside >= area.falloffMeters) continue;
        const t = 1 - outside / area.falloffMeters;
        const weight = t * t * (3 - 2 * t);
        weightSum += weight;
        targetSum += weight * (op.mode === 'relative' ? current + area.elevationMeters : area.elevationMeters);
      }
      if (weightSum <= 0) continue;

      const blend = Math.min(1, weightSum);
      const target = targetSum / weightSum;
      const next = Math.min(maxHeight, Math.max(minHeight, current * (1 - blend) + target * blend));
      if (next === current) continue;
      after[index] = next;
      touched += 1;
      maxDelta = Math.max(maxDelta, Math.abs(next - current));
    }
  }
  if (touched === 0) return;

  const vertexArea =
    (field.widthMeters / Math.max(1, field.cols - 1)) * (field.lengthMeters / Math.max(1, field.rows - 1));
  report.sculptedAreaMeters += round(touched * vertexArea, 1);
  report.maxHeightChangeMeters = Math.max(report.maxHeightChangeMeters, round(maxDelta, 2));

  commands.push(
    new SculptStrokeCommand(
      deps.terrain,
      rect,
      before,
      after,
      'Sculpt terrain',
      deps.onGroundChanged,
    ),
  );
}

/** Vertex rectangle a control point can reach. */
function vertexBounds(
  cols: number,
  rows: number,
  widthMeters: number,
  lengthMeters: number,
  point: { x: number; z: number; radiusMeters: number },
): SurfaceRect {
  const perCol = widthMeters / Math.max(1, cols - 1);
  const perRow = lengthMeters / Math.max(1, rows - 1);
  const minCol = Math.floor((point.x - point.radiusMeters + widthMeters / 2) / perCol);
  const maxCol = Math.ceil((point.x + point.radiusMeters + widthMeters / 2) / perCol);
  const minRow = Math.floor((point.z - point.radiusMeters + lengthMeters / 2) / perRow);
  const maxRow = Math.ceil((point.z + point.radiusMeters + lengthMeters / 2) / perRow);
  return clipRect(
    { x: minCol, y: minRow, width: maxCol - minCol + 1, height: maxRow - minRow + 1 },
    cols,
    rows,
  );
}

/* ---------------------------------- Paint ---------------------------------- */

/**
 * Rasterize paint regions into the live splat map.
 *
 * `paintSurfaces` is the pipeline's own rasterizer, reused rather than reimplemented so a wand-
 * painted path is byte-identical to a generated one. It writes in place, which is why this measures
 * the "after" bytes and then puts the map back: the alternative is a second copy of a signed-distance
 * feathering routine, and two copies of that would eventually disagree about what an edge is.
 */
function compilePaint(
  regions: readonly SurfaceRegionPlan[],
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
): void {
  const map = deps.terrain.surface;
  let rect: SurfaceRect = { ...EMPTY_RECT };
  for (const region of regions) {
    rect = unionRect(rect, texelBounds(map.width, map.height, map.widthMeters, map.lengthMeters, region));
  }
  rect = clipRect(rect, map.width, map.height);
  if (isEmptyRect(rect)) {
    report.warnings.push({ message: 'The wand painted nothing that lands on this site.' });
    return;
  }

  const before = map.readRect(rect);
  const warnings: CompileWarning[] = [];
  const painted = paintSurfaces(map, { regions: [...regions], notes: '' }, warnings);
  const after = map.readRect(rect);
  map.writeRect(rect, before);

  report.warnings.push(...warnings);
  if (painted === 0) return;
  report.paintedRegions += painted;

  commands.push(
    new PaintStrokeCommand(deps.terrain, rect, before, after, 'Paint ground'),
  );
}

function texelBounds(
  width: number,
  height: number,
  widthMeters: number,
  lengthMeters: number,
  region: { polygon: [number, number][]; featherMeters: number },
): SurfaceRect {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const [x, z] of region.polygon) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }
  if (!Number.isFinite(minX)) return { ...EMPTY_RECT };

  const feather = Math.max(1e-3, region.featherMeters);
  const minCol = Math.floor(((minX - feather) / widthMeters + 0.5) * width);
  const maxCol = Math.ceil(((maxX + feather) / widthMeters + 0.5) * width);
  const minRow = Math.floor(((minZ - feather) / lengthMeters + 0.5) * height);
  const maxRow = Math.ceil(((maxZ + feather) / lengthMeters + 0.5) * height);
  return clipRect(
    { x: minCol, y: minRow, width: maxCol - minCol + 1, height: maxRow - minRow + 1 },
    width,
    height,
  );
}

/* ---------------------------------- Plant ---------------------------------- */

/**
 * Expand planting masses through the shared placement engine.
 *
 * The one thing this does that the pipeline compiler does not: it holds new planting clear of the
 * plants **already on the site**. Without that, "add a few more grasses here" would drop them into
 * the bed that is already there, because the engine's only memory of what is planted is the list it
 * was given.
 *
 * ## Why that keep-out is by size class and by current crown
 *
 * This started as a line in the engine's own occupancy list — every established plant pushed into
 * every layer the spell used, reserving twice its *mature* footprint. Both halves of that were
 * wrong, and together they made the common case impossible.
 *
 * `tooClose` takes the larger of the two spacings, and layers are what stop a shrub competing with
 * a tree. Writing an established tree into every layer therefore made it compete with everything:
 * the catalog's canopy trees reach a 9 m footprint, so one mature oak put an 18 m exclusion zone
 * over the garden for grasses, perennials and groundcover alike. "Underplant this maple" — a
 * starter this feature ships — could not place a single plant, and the report blamed the ground.
 *
 * So the test is a hook of its own, with the two corrections the failure argued for:
 *
 * - **Size class, from the species' own category**, not the spell's layer names. A perennial is
 *   checked against established perennials and grasses, never against the tree it is going under.
 *   Across classes only the stem is reserved, because a hosta cannot be planted through a trunk but
 *   can perfectly well be planted under a canopy.
 * - **The crown it has now**, from `growthState`, not the one it will have in thirty years. A
 *   20 %-grown maple is a sapling, and reserving nine metres around it describes a tree that is not
 *   there yet.
 */
function compilePlant(
  plantings: readonly PlanPlanting[],
  deps: WandApplyDeps,
  rand: () => number,
  commands: Command[],
  report: WandReport,
): void {
  const terrain = deps.terrain;
  const established = establishedPlants(deps);
  const classOf = speciesClassifier(deps.catalog);
  const refusals = new Map<PlacementRefusal, number>();

  const context: PlacementContext = {
    field: terrain.field,
    halfX: terrain.widthMeters / 2,
    halfZ: terrain.lengthMeters / 2,
    siteObjects: deps.siteObjects.all(),
    placed: [],
    rand,
    plantable: (x, z) => isPlantableSurface(terrain.sampleSurface(x, z)),
    occupied: (x, z, planting, footprintRadius) => {
      const sizeClass = classOf(planting.speciesId);
      for (const plant of established) {
        // Same class: the two crowns must not overlap. Different class: only the stem is in the
        // way, which is what makes a shade bed under a tree possible at all.
        const need =
          plant.sizeClass === sizeClass
            ? plant.crownRadiusMeters + footprintRadius
            : plant.stemRadiusMeters + footprintRadius * 0.4;
        if ((plant.x - x) ** 2 + (plant.z - z) ** 2 < need * need) return true;
      }
      return false;
    },
    onRefusal: (reason) => refusals.set(reason, (refusals.get(reason) ?? 0) + 1),
  };

  const added: FloraInstance[] = [];
  for (const planting of plantings) {
    if (!deps.catalog.has(planting.speciesId)) {
      report.warnings.push({
        planting: planting.name,
        message: `“${planting.speciesId}” is not in this build's plant catalog — ${planting.name} was skipped.`,
      });
      continue;
    }
    const footprintRadius = deps.catalog.get(planting.speciesId).placement.footprintRadiusMeters;
    const { targets, retryable } = targetPositions(planting, context);
    if (targets.length === 0) {
      report.warnings.push({
        planting: planting.name,
        message: `${planting.name} described no area, region, drift or line, so nothing was planted.`,
      });
      continue;
    }

    refusals.clear();
    let placed = 0;
    for (const target of targets) {
      const instance = tryPlace(planting, target, retryable, context, footprintRadius, {});
      if (!instance) continue;
      added.push(instance);
      placed += 1;
    }
    if (placed < targets.length * 0.6) {
      report.warnings.push({
        planting: planting.name,
        message:
          `${planting.name}: only ${placed} of ${targets.length} fitted — ` +
          `${refusalReason(refusals, planting)}.`,
      });
    }
  }

  if (added.length === 0) return;
  report.plantsAdded += added.length;
  commands.push(
    new AddManyFloraCommand(
      deps.world,
      added,
      added.length === 1 ? 'Plant 1 plant' : `Plant ${added.length} plants`,
    ),
  );
}

/** How much room a plant on the site is actually taking up, and what it competes with. */
interface EstablishedPlant {
  x: number;
  z: number;
  sizeClass: PlantSizeClass;
  /** Half the spread it has grown to, not half the spread its species will reach. */
  crownRadiusMeters: number;
  /** The part of it nothing can be planted through, whatever size class is asking. */
  stemRadiusMeters: number;
}

/**
 * The three brackets that actually compete for ground.
 *
 * Derived from the species' catalog category rather than from the spell's `layer` strings, because
 * an established plant has no layer — it was placed by a tool, or by a spell whose layer names are
 * long gone. The model's layer vocabulary stays free-form and keeps doing its real job, which is
 * arbitrating between the masses of *one* spell.
 */
type PlantSizeClass = 'tree' | 'shrub' | 'ground';

function sizeClassFor(category: string): PlantSizeClass {
  if (category.endsWith('tree')) return 'tree';
  if (category.endsWith('shrub')) return 'shrub';
  return 'ground';
}

function establishedPlants(deps: WandApplyDeps): EstablishedPlant[] {
  return deps.world.all().map((instance) => {
    const definition = deps.catalog.find(instance.floraDefinitionId);
    const spread = definition ? deps.world.growthState(instance).spreadMeters : 1;
    const crownRadiusMeters = Math.max(0.15, spread / 2);
    return {
      x: instance.position.x,
      z: instance.position.z,
      sizeClass: sizeClassFor(definition?.category ?? 'perennial'),
      crownRadiusMeters,
      // A trunk, a crown of stems, a clump — the part that is solid rather than leafy. Capped
      // because even a veteran oak is something you can plant within a metre of.
      stemRadiusMeters: Math.min(0.5, crownRadiusMeters * 0.25),
    };
  });
}

/** Species id → size class, memoised: `occupied` runs once per placement attempt. */
function speciesClassifier(catalog: FloraCatalog): (speciesId: string) => PlantSizeClass {
  const memo = new Map<string, PlantSizeClass>();
  return (speciesId) => {
    const cached = memo.get(speciesId);
    if (cached) return cached;
    const sizeClass = sizeClassFor(catalog.find(speciesId)?.category ?? 'perennial');
    memo.set(speciesId, sizeClass);
    return sizeClass;
  };
}

/**
 * Name the reason a mass did not fit, rather than listing every reason it might not have.
 *
 * The engine counts refusals as it makes them, so the dominant one is a fact and not a guess. The
 * old message offered four possibilities at once, which meant a designer looking at an empty bed
 * had to work out which of them had happened — and the most common answer, "the plants already
 * there", was not among the four.
 */
function refusalReason(refusals: Map<PlacementRefusal, number>, planting: PlanPlanting): string {
  let worst: PlacementRefusal | null = null;
  let most = 0;
  for (const [reason, count] of refusals) {
    if (count > most) {
      most = count;
      worst = reason;
    }
  }

  switch (worst) {
    case 'established':
      return 'the plants already there fill it';
    case 'crowded':
      return `${planting.minSpacingMeters} m spacing needs more room than the area has`;
    case 'ground':
      return 'the ground there refuses planting — water, blacktop or paving';
    case 'structure':
      return 'a building or paving covers it';
    case 'off-site':
      return 'it falls outside the site';
    case 'outside-area':
      return 'the drawn area is too small for that many';
    default:
      return 'there was no room';
  }
}

/* ------------------------------ Remove / adjust ------------------------------ */

function compileRemove(
  op: RemovePlantsOp,
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
): void {
  const instances = resolveTarget(op.target, deps);
  if (instances.length === 0) {
    report.warnings.push({ message: 'The wand asked to remove plants, but none matched.' });
    return;
  }
  const removable = instances.filter((instance) => !instance.locked);
  if (removable.length < instances.length) {
    report.warnings.push({
      message: `${instances.length - removable.length} matching plant(s) are locked and were left alone.`,
    });
  }
  if (removable.length === 0) return;
  report.plantsRemoved += removable.length;
  commands.push(new RemoveFloraCommand(deps.world, removable));
}

function compileAdjust(
  op: AdjustPlantsOp,
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
): void {
  const instances = resolveTarget(op.target, deps);
  if (instances.length === 0) {
    report.warnings.push({ message: 'The wand asked to change plants, but none matched.' });
    return;
  }

  const changes: FloraChange[] = [];
  for (const before of instances) {
    if (before.locked && op.patch.locked !== false) continue;
    const after: FloraInstance = {
      ...before,
      position: { ...before.position },
      rotation: { ...before.rotation },
    };

    // Overgrowth is permitted, matching the inspector's own maturity slider — a wand that could not
    // reach a value the user can drag to would be an arbitrary second rule about the same field.
    if (op.patch.maturity !== undefined) after.maturity = clampMaturity(op.patch.maturity, true);
    if (op.patch.fullness !== undefined) after.fullness = op.patch.fullness;
    if (op.patch.health !== undefined) after.health = op.patch.health;
    if (op.patch.pruningStyle !== undefined) after.pruningStyle = op.patch.pruningStyle;
    if (op.patch.seasonOverrideDayOfYear !== undefined) {
      after.seasonOverrideDayOfYear = op.patch.seasonOverrideDayOfYear;
    }
    if (op.patch.rotationDegrees !== undefined) {
      after.rotation = { ...after.rotation, y: (op.patch.rotationDegrees * Math.PI) / 180 };
    }
    if (op.patch.hidden !== undefined) after.hidden = op.patch.hidden;
    if (op.patch.locked !== undefined) after.locked = op.patch.locked;

    if (JSON.stringify(after) === JSON.stringify(before)) continue;
    changes.push({ before, after });
  }

  if (changes.length === 0) {
    report.warnings.push({ message: 'The wand’s change left every matching plant as it was.' });
    return;
  }
  report.plantsAdjusted += changes.length;
  commands.push(
    new TransformFloraCommand(
      deps.world,
      changes,
      changes.length === 1 ? 'Adjust plant' : `Adjust ${changes.length} plants`,
    ),
  );
}

/**
 * Resolve a selector to instances.
 *
 * Selectors intersect, and an empty result is returned rather than widened. That is the point of
 * having no "everything" target: a selector the model got wrong removes nothing and says so, instead
 * of removing the site.
 */
export function resolveTarget(target: PlantTarget, deps: WandApplyDeps): FloraInstance[] {
  let instances = deps.world.all();

  if (target.selected === true) {
    const selected = new Set(deps.selectedIds());
    instances = instances.filter((instance) => selected.has(instance.id));
  }
  if (target.ids && target.ids.length > 0) {
    const ids = new Set(target.ids);
    instances = instances.filter((instance) => ids.has(instance.id));
  }
  if (target.speciesIds && target.speciesIds.length > 0) {
    const species = new Set(target.speciesIds);
    instances = instances.filter((instance) => species.has(instance.floraDefinitionId));
  }
  if (target.within && target.within.length >= 3) {
    const ring = target.within.map(([x, z]) => ({ x, z }));
    instances = instances.filter((instance) =>
      pointInRing(ring, instance.position.x, instance.position.z),
    );
  }
  const near = target.near;
  if (near) {
    instances = instances.filter(
      (instance) =>
        Math.hypot(instance.position.x - near.x, instance.position.z - near.z) <= near.radiusMeters,
    );
    instances.sort(
      (a, b) =>
        Math.hypot(a.position.x - near.x, a.position.z - near.z) -
        Math.hypot(b.position.x - near.x, b.position.z - near.z),
    );
  }
  if (target.limit !== undefined) instances = instances.slice(0, target.limit);
  return instances;
}

/* ---------------------------------- Build ---------------------------------- */

function compileBuild(
  structures: readonly PlanStructure[],
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
  minted: Map<string, string>,
): void {
  const warnings: CompileWarning[] = [];
  // Ids are reissued: the pipeline builder honours the plan's own id, and a wand that reused
  // "shed-1" on a site that already has one would collide with an object the user placed earlier.
  //
  // The plan id is not simply discarded, though — it is the only handle a `tag` op in the same spell
  // has on a thing that did not exist when the spell was written, so the mapping from plan id to the
  // id actually minted is recorded for `compileTags` to resolve against.
  const reissued = structures.map((structure) => {
    const id = createEntityId('site-');
    if (structure.id) minted.set(structure.id, id);
    return { ...structure, id };
  });

  const objects = buildStructures({ structures: reissued }, deps.terrain.field, warnings);
  report.warnings.push(...warnings);
  if (objects.length === 0) return;

  report.structuresAdded += objects.length;
  commands.push(
    new AddSiteObjectsCommand(
      deps.siteObjects,
      objects,
      objects.length === 1 ? `Add ${objects[0]?.name ?? 'structure'}` : `Add ${objects.length} structures`,
    ),
  );
}

/* ----------------------------------- Tags ----------------------------------- */

/**
 * Turn described tags into the same `AddTagCommand` the tag tool produces.
 *
 * Three resolutions happen here, and each one refuses rather than guesses:
 *
 * - **`structureRef`** is matched against the ids minted by a `build` op earlier in this same spell.
 *   An unresolvable ref is dropped and reported: a tag that silently lands on nothing is a sentence
 *   that will be in every future render prompt describing an object that is not there.
 * - **`structureId` / `plantId`** must name something the site currently holds, checked against the
 *   live worlds rather than trusted, because the model is quoting a context brief that was true when
 *   the request was sent.
 * - **`attachImageIndex`** is resolved against the photographs the designer attached to this turn.
 *   Out of range is dropped, not clamped — see the note on the op.
 */
function compileTags(
  op: TagOp,
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
  minted: Map<string, string>,
): void {
  const attachments = deps.attachments ?? [];

  for (const planTag of op.tags) {
    const target = resolveTagTarget(planTag, deps, minted, report);
    if (!target) continue;

    const images: TagImage[] = [];
    if (planTag.attachImageIndex !== undefined) {
      const image = attachments[planTag.attachImageIndex - 1];
      if (image) {
        images.push({
          ...image,
          ...(planTag.imageIntent ? { intent: planTag.imageIntent } : {}),
        });
      } else {
        report.warnings.push({
          message:
            `The wand pointed “${planTag.label}” at attached image ${planTag.attachImageIndex}, ` +
            `but ${attachments.length === 0 ? 'nothing was attached' : `only ${attachments.length} were`}. ` +
            'The tag was kept without a picture.',
        });
      }
    }

    const tag = createSemanticTag({
      target,
      label: planTag.label,
      category: planTag.category,
      emphasis: planTag.emphasis,
      ...(planTag.note !== undefined ? { note: planTag.note } : {}),
      ...(images.length > 0 ? { images } : {}),
    });

    commands.push(new AddTagCommand(deps.tags, tag, `Tag: ${tag.label}`));
    report.tagsAdded += 1;
    if (images.length > 0) report.tagImagesAttached += images.length;
  }
}

function resolveTagTarget(
  planTag: PlanTag,
  deps: WandApplyDeps,
  minted: Map<string, string>,
  report: WandReport,
): TagTarget | null {
  if (planTag.structureRef !== undefined) {
    const id = minted.get(planTag.structureRef);
    if (id) return { kind: 'structure', objectId: id };
    report.warnings.push({
      message: `The wand tagged “${planTag.structureRef}”, which it never built. The tag was dropped.`,
    });
    return null;
  }

  if (planTag.structureId !== undefined) {
    if (deps.siteObjects.get(planTag.structureId)) {
      return { kind: 'structure', objectId: planTag.structureId };
    }
    report.warnings.push({
      message: `The wand tagged structure ${planTag.structureId}, which is not on this site. The tag was dropped.`,
    });
    return null;
  }

  if (planTag.plantId !== undefined) {
    if (deps.world.get(planTag.plantId)) return { kind: 'plant', instanceId: planTag.plantId };
    report.warnings.push({
      message: `The wand tagged plant ${planTag.plantId}, which is not on this site. The tag was dropped.`,
    });
    return null;
  }

  if (planTag.groundLayer !== undefined) {
    if (findSurfaceLayer(planTag.groundLayer)) return { kind: 'ground', layer: planTag.groundLayer };
    report.warnings.push({
      message: `The wand tagged the ground material “${planTag.groundLayer}”, which does not exist. The tag was dropped.`,
    });
    return null;
  }

  if (planTag.location !== undefined) {
    return {
      kind: 'location',
      x: planTag.location.x,
      z: planTag.location.z,
      radiusMeters: planTag.location.radiusMeters,
    };
  }

  if (planTag.site === true) return { kind: 'site' };
  return null;
}

/* -------------------------------- Environment -------------------------------- */

function compileEnvironment(
  op: Extract<WandOp, { kind: 'environment' }>,
  deps: WandApplyDeps,
  commands: Command[],
  report: WandReport,
): void {
  const current = deps.environment.state;
  const next: EnvironmentState = {
    ...current,
    time: { ...current.time },
    date: { ...current.date },
  };

  if (op.hour !== undefined) next.time.hour = op.hour;
  if (op.minute !== undefined) next.time.minute = op.minute;
  if (op.season !== undefined) next.date.season = op.season;
  if (op.dayOfYear !== undefined) {
    next.date.dayOfYear = op.dayOfYear;
    // A specific date is meaningless in simple mode, which snaps to four anchors — asking for
    // "late October" and getting "autumn" is not what was requested.
    next.date.mode = 'calendar';
  }
  if (op.weather !== undefined) next.weather = op.weather;
  if (op.latitudeDegrees !== undefined) next.latitudeDegrees = op.latitudeDegrees;

  if (JSON.stringify(next) === JSON.stringify(current)) return;
  report.environmentChanged = true;
  commands.push(new SetEnvironmentCommand(deps.environment, next, 'Change environment'));
}

/* --------------------------------- Reporting --------------------------------- */

/**
 * The result line the panel shows under the wand's own sentence.
 *
 * Deliberately a count of things, not prose. The model's summary says what it *meant*; this says
 * what the site now contains that it did not before, and the two disagreeing is exactly the signal a
 * user needs.
 */
/**
 * Whether the site is exactly as it was.
 *
 * Its own predicate rather than a string comparison against {@link describeWandReport}, because the
 * panel colours this case as a *problem* — the wand has just described work in the line above and
 * the site does not contain it — and a UI decision that turns on matching a sentence would break
 * silently the first time that sentence is reworded.
 */
export function wandReportIsEmpty(report: WandReport): boolean {
  return (
    report.plantsAdded === 0 &&
    report.plantsRemoved === 0 &&
    report.plantsAdjusted === 0 &&
    report.structuresAdded === 0 &&
    report.structuresRemoved === 0 &&
    report.tagsAdded === 0 &&
    report.sculptedAreaMeters === 0 &&
    report.paintedRegions === 0 &&
    !report.environmentChanged
  );
}

export function describeWandReport(report: WandReport): string {
  const parts: string[] = [];
  if (report.plantsAdded > 0) parts.push(`+${report.plantsAdded} plants`);
  if (report.plantsRemoved > 0) parts.push(`−${report.plantsRemoved} plants`);
  if (report.plantsAdjusted > 0) {
    parts.push(`${report.plantsAdjusted} plant${report.plantsAdjusted === 1 ? '' : 's'} changed`);
  }
  if (report.sculptedAreaMeters > 0) {
    parts.push(
      `ground reshaped over ${Math.round(report.sculptedAreaMeters)} m² ` +
        `(up to ${report.maxHeightChangeMeters.toFixed(2)} m)`,
    );
  }
  if (report.paintedRegions > 0) {
    parts.push(`${report.paintedRegions} area${report.paintedRegions === 1 ? '' : 's'} repainted`);
  }
  if (report.structuresAdded > 0) parts.push(`+${report.structuresAdded} built`);
  if (report.structuresRemoved > 0) parts.push(`−${report.structuresRemoved} built`);
  if (report.tagsAdded > 0) {
    const images =
      report.tagImagesAttached > 0
        ? ` (${report.tagImagesAttached} with a reference photo)`
        : '';
    parts.push(`+${report.tagsAdded} tag${report.tagsAdded === 1 ? '' : 's'}${images}`);
  }
  if (report.environmentChanged) parts.push('environment moved');
  return parts.length > 0 ? parts.join(' · ') : 'nothing changed';
}

function round(value: number, places = 3): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
